import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, Building2, Clock3, X } from "lucide-react";

const KPI_CHIPS = [
  { key: "in_progress", label: "In Progress", cls: "border-indigo-500/30 bg-indigo-500/10 text-indigo-300" },
  { key: "pending", label: "Pending", cls: "border-amber-500/30 bg-amber-500/10 text-amber-300" },
  { key: "overdue", label: "Overdue", cls: "border-rose-500/30 bg-rose-500/10 text-rose-300" },
  { key: "completed", label: "Completed", cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" },
];

function formatStatus(status) {
  return status?.replace(/_/g, " ");
}

function formatCurrency(value) {
  return `Rs. ${Number(value || 0).toLocaleString("en-IN")}`;
}

export default function EmployeeInsightsDrawer({ employee, onClose }) {
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  if (!employee) return null;

  const kpi = employee.kpi || {};
  const recentTasks = employee.recent_tasks || [];

  const goTo = (url) => {
    navigate(url);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-slate-800 px-5 py-4">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-slate-800 text-base font-bold text-white">
              {(employee.full_name || "?").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-100">{employee.full_name}</p>
              <p className="truncate text-xs text-slate-400">{employee.email}</p>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                {employee.department && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
                    <Building2 className="h-3 w-3" /> {employee.department}
                  </span>
                )}
                {employee.designation && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
                    <Briefcase className="h-3 w-3" /> {employee.designation}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close employee insights"
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-800 hover:text-slate-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Task KPIs</p>
            <div className="grid grid-cols-2 gap-2">
              {KPI_CHIPS.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => goTo(`/tasks?assigned_to=${employee.id}&kpi_status=${chip.key}`)}
                  className={`flex items-center justify-between rounded-xl border px-3 py-2 text-left transition hover:brightness-125 ${chip.cls}`}
                >
                  <span className="text-xs font-medium">{chip.label}</span>
                  <span className="text-base font-bold">{kpi[chip.key] || 0}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Expenses</p>
            <div className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-900/60 px-3 py-2">
              <span className="text-xs text-slate-300">{employee.expense_total || 0} submitted</span>
              <span className="text-sm font-semibold text-slate-200">{formatCurrency(employee.expense_amount)}</span>
            </div>
          </div>

          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Recent Tasks</p>
            {recentTasks.length === 0 ? (
              <p className="text-xs text-slate-500">No tasks assigned yet.</p>
            ) : (
              <div className="space-y-1.5">
                {recentTasks.map((task) => (
                  <button
                    key={task.id}
                    type="button"
                    onClick={() => goTo(task.url)}
                    className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-800 bg-slate-900/60 px-3 py-2 text-left transition hover:bg-slate-900"
                  >
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                        {task.task_code}
                      </p>
                      {task.title ? (
                        <p className="truncate text-xs font-medium text-slate-200">{task.title}</p>
                      ) : (
                        <p className="truncate text-xs italic text-slate-500">Not visible to you</p>
                      )}
                    </div>
                    <span className="flex-shrink-0 rounded-full bg-sky-500/10 px-2 py-0.5 text-[11px] capitalize text-sky-300">
                      {formatStatus(task.status)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex items-center gap-2 border-t border-slate-800 px-5 py-4">
          <button
            type="button"
            onClick={() => goTo(employee.performance_url)}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-sky-600 py-2.5 text-sm font-semibold text-white transition hover:bg-sky-500"
          >
            <Clock3 className="h-4 w-4" />
            View Performance
          </button>
          <button
            type="button"
            onClick={() => goTo(employee.url)}
            className="flex-1 rounded-xl border border-slate-700 py-2.5 text-sm font-semibold text-slate-200 transition hover:bg-slate-800"
          >
            Manage User
          </button>
        </div>
      </div>
    </div>
  );
}
